import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { authApi } from "../utils/httpClient";
import type { Client } from "../api/clients";
import type { ApiResponse } from "@industronics/fids-utils";

function addOneYear(d: Date) {
  const x = new Date(d);
  x.setFullYear(x.getFullYear() + 1);
  return x;
}

function toIsoDateInput(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

type Props = {
  open: boolean;
  client: Client | null;
  onClose: () => void;
  onSuccess: () => void;
};

export default function RenewPlanModal({ open, client, onClose, onSuccess }: Props) {
  const [startAt, setStartAt] = useState(toIsoDateInput(new Date()));
  const [endAt, setEndAt] = useState(toIsoDateInput(addOneYear(new Date())));

  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // derive plan info once per client
  const { planId, planKey, planStatus, isNotActivated, modalTitle, actionText } = useMemo(() => {
    const currentPlan = (client as any)?.currentPlan;
    const planId = currentPlan?.planId ? String(currentPlan.planId) : "";
    const planKey = currentPlan?.planKey ? String(currentPlan.planKey) : "";
    const planStatus = currentPlan?.status as string | undefined;

    const isNotActivated =
      planStatus === "NOT_ACTIVATED" || planStatus === "NOT ACTIVATED";

    return {
      planId,
      planKey,
      planStatus,
      isNotActivated,
      modalTitle: isNotActivated ? "Activate plan" : "Renew plan",
      actionText: isNotActivated ? "Activate plan" : "Renew plan",
    };
  }, [client]);

  useEffect(() => {
    if (!open) return;

    // Reset every time modal opens
    setError(null);
    setSubmitting(false);
    setFile(null);
    setAmount("");
    setInvoiceNo("");
    setCurrency("USD");

    const now = new Date();
    setStartAt(toIsoDateInput(now));
    setEndAt(toIsoDateInput(addOneYear(now)));
  }, [open]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open || !client) return null;

  const missingPlan = !planId || !planKey;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (missingPlan) return;

    if (!file) {
      setError("Please upload invoice file");
      return;
    }

    // backend expects ISO strings; use UTC midnight bounds
    const startIso = new Date(`${startAt}T00:00:00.000Z`).toISOString();
    const endIso = new Date(`${endAt}T23:59:59.000Z`).toISOString();

    try {
      setSubmitting(true);

      // 1) Create billing transaction (multipart)
      const fd = new FormData();
      fd.append("file", file);
      fd.append("clientId", client._id);
      fd.append("planId", planId);
      fd.append("periodStart", startIso);
      fd.append("periodEnd", endIso);

      if (amount.trim()) fd.append("amount", amount.trim());
      if (currency.trim()) fd.append("currency", currency.trim());
      if (invoiceNo.trim()) fd.append("invoiceNo", invoiceNo.trim());

      const txRes = await authApi.post<ApiResponse>("/billing-transactions/create", fd);

      if (!txRes.data.success) {
        throw new Error(txRes.data?.message || "Failed to create billing transaction");
      }

      const tx = txRes.data.data as any;
      const billingTransactionId = tx?._id;
      if (!billingTransactionId) throw new Error("Billing transaction id not returned");

      // 2) Apply renew/activate
      const renewRes = await authApi.post<ApiResponse>(
        `/clients/${client._id}/subscription/plan/renew`,
        { billingTransactionId }
      );

      if (!renewRes.data.success) {
        throw new Error(renewRes.data?.message || "Failed to renew plan");
      }

      toast.success(isNotActivated ? "Plan activated successfully" : "Plan renewed successfully");
      onSuccess();
      onClose();
    } catch (e: any) {
      const msg = e?.message || "Failed to renew plan";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        // click outside closes
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form onSubmit={submit} className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">
              {missingPlan ? "Plan not found" : modalTitle}
            </h2>
            <p className="text-sm text-gray-500">
              Client:{" "}
              <span className="font-medium text-gray-800">
                {(client as any)?.name || client.email}
              </span>
            </p>
          </div>

          <button
            type="button"
            className="rounded-lg border px-3 py-1 text-sm hover:bg-gray-50"
            onClick={onClose}
            disabled={submitting}
          >
            Close
          </button>
        </div>

        {missingPlan ? (
          <div className="mb-4 whitespace-pre-line rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            This client does not have a selected plan. Please edit the client and assign a plan first.
          </div>
        ) : error ? (
          <div className="mb-4 whitespace-pre-line rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {!missingPlan && (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Selected plan
                </label>

                <div className="rounded-xl border bg-gray-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{planKey} Plan</p>
                      <p className="mt-1 text-xs text-gray-600">
                        Status:{" "}
                        <span className="font-medium">
                          {isNotActivated ? "Not activated" : planStatus === "ACTIVE" ? "Active" : "Expired"}
                        </span>
                      </p>
                    </div>

                    <span className="inline-flex rounded-full border px-2 py-1 text-xs">PLAN</span>
                  </div>

                  {isNotActivated ? (
                    <p className="mt-2 text-xs text-purple-700">
                      Upload the invoice below to activate this plan.
                    </p>
                  ) : (
                    <p className="mt-2 text-xs text-gray-500">
                      This will update the plan validity period.
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Start date
                </label>
                <input
                  type="date"
                  className="w-full rounded-xl border px-3 py-2"
                  value={startAt}
                  onChange={(e) => setStartAt(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  End date
                </label>
                <input
                  type="date"
                  className="w-full rounded-xl border px-3 py-2"
                  value={endAt}
                  onChange={(e) => setEndAt(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Amount (optional)
                </label>
                <input
                  className="w-full rounded-xl border px-3 py-2"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="4800"
                  disabled={submitting}
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Currency (optional)
                </label>
                <input
                  className="w-full rounded-xl border px-3 py-2"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  placeholder="USD"
                  disabled={submitting}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Invoice No (optional)
                </label>
                <input
                  className="w-full rounded-xl border px-3 py-2"
                  value={invoiceNo}
                  onChange={(e) => setInvoiceNo(e.target.value)}
                  placeholder="INV-2026-0001"
                  disabled={submitting}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Invoice file (required)
                </label>
                <input
                  type="file"
                  className="w-full rounded-xl border px-3 py-2"
                  accept=".pdf,image/png,image/jpeg"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  disabled={submitting}
                />
                <p className="mt-1 text-xs text-gray-500">Allowed: PDF, PNG, JPG (max 15MB)</p>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border px-4 py-2 hover:bg-gray-50"
                disabled={submitting}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="rounded-xl bg-black px-4 py-2 text-white hover:opacity-90 disabled:opacity-60"
                disabled={submitting}
              >
                {submitting ? "Processing…" : actionText}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
